import React from 'react'
import { Box, Grid, generateUtilityClasses, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { CircularProgress } from '@mui/material'
import { styled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { appRoute } from '../../../../../constants'
import { stableSort, getComparator } from '../../../../../utils/sorting'
import TableSortLabel from '@mui/material/TableSortLabel'
import { visuallyHidden } from '@mui/utils'
import { storageGet, storageSet } from '../../../../../utils/storage'
import { formatNumber } from '../../../../../utils/general'
import { UspReportAccountsChargeOffsResult } from '../../../../../models'
import { convertDateToDisplay } from '../../../../../utils/dateUtils'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'tableHead', 'tableCellName'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    margin: theme.spacing(3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },

  [`& .${classes.tableHead}`]: {
    minWidth: '170px',
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
}))

interface Props {
  filterData: UspReportAccountsChargeOffsResult[]
  isLoading: boolean
}

const ReceivablesRecencyTableBlock = ({ filterData, isLoading }: Props) => {
  const navigate = useNavigate()
  const initialOrder = (storageGet('reports_receivables_recency_ar_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('reports_receivables_recency_ar_order_by') || 'Customer') as keyof UspReportAccountsChargeOffsResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspReportAccountsChargeOffsResult>(initialOrderBy)

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspReportAccountsChargeOffsResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('reports_receivables_recency_ar_order', isAsc ? 'desc' : 'asc')
    storageSet('reports_receivables_recency_ar_order_by', property)
  }

  const createSortHandler = (property: keyof UspReportAccountsChargeOffsResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }
  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 118px)' }}>
      <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'StockNumber' ? order : false}>
              <TableSortLabel
                active={orderBy === 'StockNumber'}
                direction={orderBy === 'StockNumber' ? order : 'asc'}
                onClick={createSortHandler('StockNumber')}
              >
                Stock #
                {orderBy === 'StockNumber' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Customer' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Customer'}
                direction={orderBy === 'Customer' ? order : 'asc'}
                onClick={createSortHandler('Customer')}
              >
                Customer
                {orderBy === 'Customer' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'TransactionType' ? order : false}>
              <TableSortLabel
                active={orderBy === 'TransactionType'}
                direction={orderBy === 'TransactionType' ? order : 'asc'}
                onClick={createSortHandler('TransactionType')}
              >
                Transaction Type
                {orderBy === 'TransactionType' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'EffectiveDate' ? order : false}>
              <TableSortLabel
                active={orderBy === 'EffectiveDate'}
                direction={orderBy === 'EffectiveDate' ? order : 'asc'}
                onClick={createSortHandler('EffectiveDate')}
              >
                Effective Date
                {orderBy === 'EffectiveDate' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'AccountNumber' ? order : false}>
              <TableSortLabel
                active={orderBy === 'AccountNumber'}
                direction={orderBy === 'AccountNumber' ? order : 'asc'}
                onClick={createSortHandler('AccountNumber')}
              >
                Account #
                {orderBy === 'AccountNumber' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Branch' ? order : false}>
              <TableSortLabel active={orderBy === 'Branch'} direction={orderBy === 'Branch' ? order : 'asc'} onClick={createSortHandler('Branch')}>
                Branch
                {orderBy === 'Branch' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Dealer' ? order : false}>
              <TableSortLabel active={orderBy === 'Dealer'} direction={orderBy === 'Dealer' ? order : 'asc'} onClick={createSortHandler('Dealer')}>
                Dealer
                {orderBy === 'Dealer' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Collector' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Collector'}
                direction={orderBy === 'Collector' ? order : 'asc'}
                onClick={createSortHandler('Collector')}
              >
                Collector
                {orderBy === 'Collector' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalancePrincipal' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalancePrincipal'}
                direction={orderBy === 'BalancePrincipal' ? order : 'asc'}
                onClick={createSortHandler('BalancePrincipal')}
              >
                Principal
                {orderBy === 'BalancePrincipal' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalanceGross' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalanceGross'}
                direction={orderBy === 'BalanceGross' ? order : 'asc'}
                onClick={createSortHandler('BalanceGross')}
              >
                Gross
                {orderBy === 'BalanceGross' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalancePickups' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalancePickups'}
                direction={orderBy === 'BalancePickups' ? order : 'asc'}
                onClick={createSortHandler('BalancePickups')}
              >
                Pickups
                {orderBy === 'BalancePickups' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalanceChargeback' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalanceChargeback'}
                direction={orderBy === 'BalanceChargeback' ? order : 'asc'}
                onClick={createSortHandler('BalanceChargeback')}
              >
                Chargeback
                {orderBy === 'BalanceChargeback' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalanceLateCharge' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalanceLateCharge'}
                direction={orderBy === 'BalanceLateCharge' ? order : 'asc'}
                onClick={createSortHandler('BalanceLateCharge')}
              >
                Late Charge
                {orderBy === 'BalanceLateCharge' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalanceSideNotes' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalanceSideNotes'}
                direction={orderBy === 'BalanceSideNotes' ? order : 'asc'}
                onClick={createSortHandler('BalanceSideNotes')}
              >
                Side Notes
                {orderBy === 'BalanceSideNotes' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'InterestDue' ? order : false}>
              <TableSortLabel
                active={orderBy === 'InterestDue'}
                direction={orderBy === 'InterestDue' ? order : 'asc'}
                onClick={createSortHandler('InterestDue')}
              >
                Interest Due
                {orderBy === 'InterestDue' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'LateChargeDue' ? order : false}>
              <TableSortLabel
                active={orderBy === 'LateChargeDue'}
                direction={orderBy === 'LateChargeDue' ? order : 'asc'}
                onClick={createSortHandler('LateChargeDue')}
              >
                Late Charge Due
                {orderBy === 'LateChargeDue' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'RepoACV' ? order : false}>
              <TableSortLabel active={orderBy === 'RepoACV'} direction={orderBy === 'RepoACV' ? order : 'asc'} onClick={createSortHandler('RepoACV')}>
                Repo ACV
                {orderBy === 'RepoACV' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'TotalLosses' ? order : false}>
              <TableSortLabel
                active={orderBy === 'TotalLosses'}
                direction={orderBy === 'TotalLosses' ? order : 'asc'}
                onClick={createSortHandler('TotalLosses')}
              >
                Total Losses
                {orderBy === 'TotalLosses' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'UnearnedDiscount' ? order : false}>
              <TableSortLabel
                active={orderBy === 'UnearnedDiscount'}
                direction={orderBy === 'UnearnedDiscount' ? order : 'asc'}
                onClick={createSortHandler('UnearnedDiscount')}
              >
                Unearned Discount
                {orderBy === 'UnearnedDiscount' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'InternalLoss' ? order : false}>
              <TableSortLabel
                active={orderBy === 'InternalLoss'}
                direction={orderBy === 'InternalLoss' ? order : 'asc'}
                onClick={createSortHandler('InternalLoss')}
              >
                Internal Loss
                {orderBy === 'InternalLoss' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BalanceDeficiency' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BalanceDeficiency'}
                direction={orderBy === 'BalanceDeficiency' ? order : 'asc'}
                onClick={createSortHandler('BalanceDeficiency')}
              >
                Deficiency
                {orderBy === 'BalanceDeficiency' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(getComparator(order, orderBy), filterData as any[]).map((item, index) => (
            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell
                className={classes.tableCellName}
                onClick={() => navigate(appRoute.InventoryEdit.replace(':carID', item?.CarID?.toString() as string))}
              >
                {item.StockNumber}
              </TableCell>
              <TableCell
                className={classes.tableCellName}
                onClick={() => navigate(appRoute.AccountsOverview.replace(':dealID', item?.DealID?.toString() as string))}
              >
                {item?.Customer}
              </TableCell>
              <TableCell>{item?.TransactionType as string}</TableCell>
              <TableCell>{convertDateToDisplay(item?.EffectiveDate as string)}</TableCell>
              <TableCell>{item?.AccountNumber}</TableCell>
              <TableCell>{item?.Branch}</TableCell>
              <TableCell>{item?.Dealer}</TableCell>
              <TableCell>{item?.Collector}</TableCell>
              <TableCell>{formatNumber(item?.BalancePrincipal as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalanceGross as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalancePickups as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalanceChargeback as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalanceLateCharge as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalanceSideNotes as string)}</TableCell>
              <TableCell>{formatNumber(item?.InterestDue as string)}</TableCell>
              <TableCell>{formatNumber(item?.LateChargeDue as string)}</TableCell>
              <TableCell>{formatNumber(item?.RepoACV as string)}</TableCell>
              <TableCell>{formatNumber(item?.TotalLosses as string)}</TableCell>
              <TableCell>{formatNumber(item?.UnearnedDiscount as string)}</TableCell>
              <TableCell>{formatNumber(item?.InternalLoss as string)}</TableCell>
              <TableCell>{formatNumber(item?.BalanceDeficiency as string)}</TableCell>
            </TableRow>
          ))}
          {filterData?.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                No results
              </TableCell>
            </TableRow>
          )}
          {isLoading && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ReceivablesRecencyTableBlock
